/**
 * CompactionMarker — slim divider shown inline in the chat where earlier
 * messages have been compacted into a summary by the AI summarizer.
 *
 * Sysadmins (and devs) get a popover with the full summary content for
 * debugging. Regular users see only the marker and label.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { useAuth } from '../../auth';

interface CompactionMarkerProps {
  content: string;
}

export const CompactionMarker: React.FC<CompactionMarkerProps> = ({ content }) => {
  const { t } = useTranslation('aiChat');
  const { user } = useAuth();
  const canDebug = import.meta.env.DEV || !!user?.is_sysadmin;

  return (
    <div className="flex items-center gap-2 py-2" role="separator" aria-label={t('compaction.markerLabel')}>
      <div className="flex-1 border-t border-border" />
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Sparkles className="h-3 w-3" />
        <span>{t('compaction.markerLabel')}</span>
        {canDebug && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="ml-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label={t('compaction.viewSummary')}
              >
                <Info className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[480px] max-w-[90vw] max-h-[60vh] overflow-y-auto" align="center">
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>{t('compaction.summaryTitle')}</span>
                </div>
                <p className="text-xs text-muted-foreground">{t('compaction.summaryHelp')}</p>
                <pre className="whitespace-pre-wrap break-words text-xs text-foreground bg-muted/40 rounded-md p-3 font-mono">
                  {content}
                </pre>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div className="flex-1 border-t border-border" />
    </div>
  );
};
